<template>
  <ViewContainer :title="$t('organization.create_organization')">
    <v-row>
      <v-col cols="12" sm="12" md="6" lg="6">
        <v-sheet elevation="2" class="px-12 py-8 rounded white">
          <v-form v-model="formOptions.valid">
            <v-text-field
              required
              outlined
              dense
              data-private
              data-testid="organizationCreate-textField-name"
              v-model="organization.name"
              :maxlength="options.nameMaxLength"
              :rules="[
                name =>
                  !!name ||
                  $t(
                    'organization_settings.your_organization_needs_to_have_a_name'
                  ),
              ]"
              :label="`${$t('organization.organization_name')}*`"
            ></v-text-field>
            <v-select
              outlined
              required
              dense
              item-text="name"
              data-testid="organizationCreate-select-language"
              v-model="organization.language"
              :rules="[
                language =>
                  !!language || $t('validation.form.this_field_is_required'),
              ]"
              :items="options.availableLanguages"
              :label="`${$t('organization.default_language')}*`"
            ></v-select>
            <v-select
              outlined
              required
              dense
              item-text="name"
              data-testid="organizationCreate-select-language"
              v-model="organization.authenticationType"
              :rules="[
                authenticationType =>
                  !!authenticationType ||
                  $t('validation.form.this_field_is_required'),
              ]"
              :items="options.authenticationTypes"
              :label="`${$t('organization.authentication_type')}*`"
            ></v-select>
            <v-text-field
              required
              outlined
              dense
              data-private
              data-testid="organizationCreate-textField-name"
              :rules="[
                maxSitesAvailable =>
                  !!maxSitesAvailable ||
                  $t('validation.form.this_field_is_required'),
                maxSitesAvailable =>
                  maxSitesAvailable <= options.maxSitesAvailable ||
                  $t('organization_settings.sites_max', {
                    value: options.maxSitesAvailable.toLocaleString(language),
                  }),
              ]"
              v-model.number="organization.sitesAvailable"
              :label="`${$t('organization.sites_available')}*`"
            ></v-text-field>
            <v-text-field
              required
              outlined
              dense
              data-private
              data-testid="organizationCreate-textField-licenses"
              v-model="organization.licenses"
              :rules="[
                maxLicenses =>
                  !!maxLicenses || $t('validation.form.this_field_is_required'),
                maxLicenses =>
                  maxLicenses <= options.maxLicenses ||
                  $t('organization_settings.licenses_max', {
                    value: options.maxLicenses.toLocaleString(language),
                  }),
              ]"
              :label="`${$t('common.license_other')}*`"
            ></v-text-field>
            <v-select
              outlined
              required
              dense
              data-testid="organizationCreate-select-type"
              v-model="organization.type"
              item-text="name"
              :rules="[
                type => !!type || $t('validation.form.this_field_is_required'),
              ]"
              :label="`${$t('common.type')}*`"
              :items="options.organizationTypes"
            ></v-select>
            <v-select
              outlined
              required
              dense
              data-testid="organizationCreate-select-media_provider"
              v-model="organization.mediaProvider"
              item-text="name"
              :rules="[
                mediaProvider =>
                  !!mediaProvider ||
                  $t('validation.form.this_field_is_required'),
              ]"
              :label="`${$t('organization.media_provider')}*`"
              :items="options.mediaProviders"
            ></v-select>
            <v-text-field
              outlined
              dense
              data-private
              data-testid="organizationCreate-textField-name"
              v-if="
                organization.mediaProvider === 'EXTERNAL_AZURE_STORAGE_SERVICE'
              "
              :required="
                organization.mediaProvider === 'EXTERNAL_AZURE_STORAGE_SERVICE'
              "
              v-model="organization.providers__azure_storage_storageURL"
              :label="
                `${$t('organization.providers__azure_storage_storageURL')}*`
              "
              :rules="[
                value =>
                  !!value || $t('validation.form.this_field_is_required'),
              ]"
            ></v-text-field>
            <v-select
              outlined
              required
              dense
              data-testid="organizationCreate-select-plan"
              v-model="organization.plan"
              item-text="name"
              :rules="[
                plan => !!plan || $t('validation.form.this_field_is_required'),
              ]"
              :label="`${$t('organization.plan')}*`"
              :items="options.organizationPlans"
            ></v-select>
            <v-radio-group
              v-model="organization.isEnabled"
              mandatory
              hide-details
              data-testid="organizationCreate-radioGroup-organizationStatus"
            >
              <p class="subtitle2">
                {{ $t('organization.organization_status') }}
              </p>
              <v-radio
                v-for="status in options.statusOptions"
                :key="status.value"
                :label="status.text"
                :value="status.value"
              ></v-radio>
            </v-radio-group>
            <v-radio-group
              v-model="organization.isDeleted"
              mandatory
              hide-details
            >
              <p class="subtitle2">
                {{ $t('organization.organization_deleted_status') }}
              </p>
              <v-radio
                v-for="status in options.deletedOptions"
                :key="status.value"
                :label="status.text"
                :value="status.value"
              ></v-radio>
            </v-radio-group>
            <v-radio-group
              v-model="organization.hasIntercomDisabled"
              mandatory
              hide-details
            >
              <p class="subtitle2">
                {{ $t('organization.has_intercom_disabled') }}
              </p>
              <v-radio
                v-for="status in options.intercomOptions"
                :key="status.value"
                :label="status.text"
                :value="status.value"
              ></v-radio>
            </v-radio-group>
            <p class="subtitle2 mt-4">
              {{ $t('organization.features_enabled') }}
            </p>
            <v-checkbox
              v-model="organization.featuresEnabled__quickAction"
              hide-details
              class="pa-0 checkbox"
              :label="$t('common.quick_action_one')"
            />
            <v-checkbox
              v-model="organization.featuresEnabled__problemSolving"
              hide-details
              class="pa-0 checkbox"
              :label="$t('common.problem_solving_one')"
            />
            <v-checkbox
              v-model="organization.featuresEnabled__routines"
              hide-details
              class="pa-0 checkbox"
              :label="$t('routines.routine_other')"
            />
            <v-checkbox
              v-model="organization.featuresEnabled__improvementIdea"
              hide-details
              class="pa-0 checkbox"
              :label="$t('common.improvement_idea_one')"
            />
            <v-checkbox
              v-model="organization.featuresEnabled__insights"
              hide-details
              class="pa-0 checkbox"
              :label="$t('insights.insights')"
            />
            <v-checkbox
              v-model="organization.featuresEnabled__translation"
              hide-details
              class="pa-0 checkbox"
              :label="$t('common.translation_other')"
            />
            <v-checkbox
              v-model="organization.featuresEnabled__boards"
              hide-details
              class="pa-0 checkbox"
              :label="$t('boards.board_other')"
            />
            <v-checkbox
              v-model="organization.featuresEnabled__tasks"
              hide-details
              class="pa-0 checkbox"
              :label="$t('tasks.task_other')"
            />
            <v-checkbox
              v-model="organization.featuresEnabled__msTeams"
              hide-details
              class="pa-0 checkbox"
              :label="$t('common.microsoft_teams')"
            />
            <CurrenciesSelector
              class="mt-8"
              :defaultCurrency="organization.currency"
              :allowedSiteCurrencies="organization.allowedSiteCurrencies"
              @setDefaultCurrency="setDefaultCurrency"
            />
            <DomainsSelector
              class="mt-8"
              :domains="organization.domains"
              @onAddDomain="addDomain"
              @onRemoveDomain="removeDomain"
            />
            <p class="subtitle2 mt-8">{{ $t('organization.logos') }}</p>
            <v-row>
              <v-col>
                <p class="rever-label">{{ $t('organization.web_logo') }}</p>
                <ThumbnailPhotoSelector
                  data-private
                  isPublic
                  isEditMode
                  :mediaObject="logo"
                  :disabled="false"
                  @onDoneUploading="onDoneUploading($event, 'logo')"
                  @onRemovePhoto="removeLogo('logo')"
                />
                <p
                  v-if="!organization.logo"
                  class="font-size-10 text-ui-600 ma-0"
                >
                  {{ $t('organization.logo_size') }}
                </p>
              </v-col>
              <v-col>
                <p class="rever-label">
                  {{ $t('organization.mobile_logo') }}
                </p>
                <ThumbnailPhotoSelector
                  data-private
                  isPublic
                  isEditMode
                  :mediaObject="mobileLogo"
                  :disabled="false"
                  @onDoneUploading="onDoneUploading($event, 'mobileLogo')"
                  @onRemovePhoto="removeLogo('mobileLogo')"
                />
                <p
                  v-if="!organization.mobileLogo"
                  class="font-size-10 text-ui-600 ma-0"
                >
                  {{ $t('organization.mobileLogo_size') }}
                </p>
              </v-col>
            </v-row>
            <div class="mt-8 text-right">
              <Button
                outlined
                size="large"
                color="grey"
                class="me-2"
                data-testid="organizationCreate-button-cancel"
                :disabled="isLoading"
                :text="$t('common.button.cancel')"
                @click="cancel"
              />
              <Button
                size="large"
                color="primary"
                data-testid="organizationCreate-button-save"
                :loading="isLoading"
                :disabled="isLoading || !formOptions.valid"
                :text="$t('common.button.save')"
                @click="onSubmit"
              />
            </div>
          </v-form>
        </v-sheet>
      </v-col>
    </v-row>
  </ViewContainer>
</template>

<script>
import mixin from '@/mixins/mixin.vue'
import ThumbnailPhotoSelector from '@/containers/ThumbnailPhotoSelector/ThumbnailPhotoSelector'
import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import CurrenciesSelector from '@/containers/Admin/OrganizationsView/CurrenciesSelector/CurrenciesSelector.vue'
import DomainsSelector from '@/containers/Admin/OrganizationsView/DomainsSelector/DomainsSelector.vue'
import Button from '@/components/UI/Button'

export default {
  name: 'OrganizationCreateView',
  components: {
    CurrenciesSelector,
    DomainsSelector,
    ThumbnailPhotoSelector,
    ViewContainer,
    Button,
  },

  mixins: [mixin],

  data() {
    return {
      isLoading: false,
      formOptions: {
        valid: true,
      },
      images: {},
      organization: {
        domains: [],
        allowedSiteCurrencies: [],
        currency: null,
        isEnabled: true,
        isDeleted: false,
        mediaProvider: 'AWS_S3',
        hasIntercomDisabled: false,
      },
      language: this.$store.getters.language,
      options: {
        nameMaxLength: this.$constants.organization.nameMaxLength,
        maxSitesAvailable: this.$constants?.organization?.maxSitesAvailable,
        maxLicenses: this.$constants?.organization?.maxLicenses,
        availableLanguages: this.getLanguages(),

        authenticationTypes: this.$constants.organization.authenticationTypes.map(
          item => {
            return {
              value: item.value,
              name: this.$t(item.name),
            }
          }
        ),
        organizationTypes: this.$constants.organization.organizationTypes,
        organizationPlans: this.$constants.organization.organizationPlans,
        mediaProviders: this.$constants.organization.mediaProviders,
        statusOptions: [
          { text: this.$t('organization.enabled'), value: true },
          { text: this.$t('organization.disabled'), value: false },
        ],
        deletedOptions: [
          { text: this.$t('organization.active'), value: false },
          { text: this.$t('organization.deleted'), value: true },
        ],
        intercomOptions: [
          { text: this.$t('common.yes'), value: true },
          { text: this.$t('common.no'), value: false },
        ],
      },
    }
  },

  computed: {
    logo() {
      return this.images?.logo ?? null
    },

    mobileLogo() {
      return this.images?.mobileLogo ?? null
    },

    showAzureSettingsFields() {
      const authenticationTypes = [
        'azure_active_directory',
        'local_and_azure_active_directory',
      ]

      return authenticationTypes.includes(this.organization.authenticationType)
    },
  },

  methods: {
    cancel() {
      this.goBack()
    },

    async onSubmit(e) {
      e.preventDefault()

      try {
        await this.$store.dispatch('createOrganization', {
          data: this.organization,
        })

        this.showSuccess(this.$t('organization.organization_saved'))
        this.navigateTo('/admin/organizations/')
      } catch (error) {
        this.showError(error.response.data.message)
      }
    },

    setDefaultCurrency(value) {
      this.organization.currency = value
    },

    setExtraCurrency(value) {
      this.organization.allowedSiteCurrencies.push(value)
    },

    onFeaturesToogle(name, value) {
      this.organization[name] = value
    },

    onDoneUploading(data, fieldName) {
      this.organization[fieldName] = data.id
      this.images = {
        ...this.images,
        [fieldName]: data,
      }
    },

    removeLogo(fieldName) {
      this.organization[fieldName] = null
      this.images[fieldName] = null
    },

    addDomain() {
      this.organization.domains = [...this.organization.domains, '']
    },

    removeDomain(index) {
      this.organization.domains.splice(index, 1)
    },
  },
}
</script>

<style lang="scss" scoped>
.trash-button {
  padding-top: 24px;
  cursor: pointer;
}

.checkbox {
  width: fit-content;
}
</style>
